/**
 * Pure helpers for the Studio Models page.
 *
 * Extracted from `index.tsx` so the filter contract is unit-testable
 * without spinning up React + the tenant-models hook. Each helper is a
 * deterministic function over plain data.
 */

export interface StudioModelRow {
  brandId: string;
  displayName: string;
  description: string;
  modality: string;
}

/**
 * #631 — search must treat `_` and `-` as the same character.
 *
 * The brand id surface is split between two conventions across the
 * platform: the catalog seed stores `argus_vision` (underscore), the
 * `/insights` route normalizes the same brand to `argus-vision`
 * (hyphen) for public-facing slugs, and the QA report (#631) showed
 * the Studio Models table displaying `argus_vision` while a search
 * for that exact string returned zero rows in some prod data shapes.
 *
 * The safe fix is to make the search itself character-class-tolerant
 * so a user who types the visible id (with whichever separator) always
 * matches the row they can see on screen. Hyphen and underscore both
 * collapse to `-` before substring matching.
 */
export function normalizeBrandSearch(input: string): string {
  return input.toLowerCase().replace(/[-_]/g, '-');
}

export function matchesModelSearch(
  row: StudioModelRow,
  search: string,
  modalityFilter: string,
): boolean {
  if (modalityFilter !== 'all' && row.modality !== modalityFilter) return false;
  if (!search) return true;
  const q = normalizeBrandSearch(search);
  return (
    normalizeBrandSearch(row.brandId).includes(q) ||
    row.displayName.toLowerCase().includes(q) ||
    row.description.toLowerCase().includes(q) ||
    row.modality.toLowerCase().includes(q)
  );
}
